import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, Calendar, Heart, Trash2 } from "lucide-react";
import { Card, Badge } from "@tabipla/ui";
import { cn } from "@tabipla/ui";
import type { TripResponseDto } from "@tabipla/shared";
import { computeTripStatus } from "@tabipla/shared";
import { getTripCover } from "@/utils/trip-cover";

interface TripCardProps {
  trip: TripResponseDto;
  onDelete?: (tripId: string) => void;
  onFavorite?: (tripId: string) => void;
  isFavorite?: boolean;
  className?: string;
}

export function TripCard({
  trip,
  onDelete,
  onFavorite,
  isFavorite = false,
  className,
}: TripCardProps) {
  const { t } = useTranslation();

  const getStatusConfig = (status: string) => {
    switch (status) {
      case "planning":
        return {
          label: t("trips.statusPlanning"),
          className: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
        };
      case "confirmed":
        return {
          label: t("trips.statusConfirmed"),
          className: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
        };
      case "completed":
        return {
          label: t("trips.statusCompleted"),
          className: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300",
        };
      default:
        return {
          label: status,
          className: "bg-gray-100 text-gray-800",
        };
    }
  };

  const formatDateRange = (startDate: string | null, endDate: string | null) => {
    if (!startDate || !endDate) {
      return {
        formatted: t("trips.dateUndecided", "日程未定"),
        days: null,
      };
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    return {
      formatted: `${start.toLocaleDateString("ja-JP", {
        month: "short",
        day: "numeric",
      })} - ${end.toLocaleDateString("ja-JP", {
        month: "short",
        day: "numeric",
      })}`,
      days: diffDays,
    };
  };

  // Compute status based on dates
  const computedStatus = computeTripStatus(trip.startDate, trip.endDate);
  const statusConfig = getStatusConfig(computedStatus);
  const dateInfo = formatDateRange(trip.startDate, trip.endDate);
  const generatedCover = getTripCover(trip.destination, trip.interests);

  // Use DB values if available, otherwise use generated values
  const coverEmoji = trip.coverEmoji ?? generatedCover.emoji;
  const coverColor = trip.coverColor ?? generatedCover.gradient;

  return (
    <Link to={`/trips/${trip.id}`} className={cn("group block", className)}>
      <Card interactive className="overflow-hidden">
        <div className="flex">
          {/* Left: Emoji Cover */}
          <div
            className={cn(
              "relative flex w-20 shrink-0 items-center justify-center bg-gradient-to-br sm:w-24",
              coverColor
            )}
          >
            <span className="text-3xl transition-transform duration-300 group-hover:scale-110 sm:text-4xl">
              {coverEmoji}
            </span>
          </div>

          {/* Right: Content */}
          <div className="flex min-w-0 flex-1 flex-col p-3">
            {/* Header: Title + Actions */}
            <div className="mb-1 flex items-start justify-between gap-2">
              <h3 className="line-clamp-1 font-semibold leading-tight transition-colors group-hover:text-primary">
                {trip.title}
              </h3>
              <div className="flex shrink-0 items-center gap-1">
                {onFavorite && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onFavorite(trip.id);
                    }}
                    className={cn(
                      "flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full transition-colors hover:bg-muted",
                      isFavorite && "text-red-500"
                    )}
                  >
                    <Heart className={cn("h-5 w-5", isFavorite && "fill-current")} />
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onDelete(trip.id);
                    }}
                    className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>

            {/* Location */}
            <div className="mb-1 flex items-center gap-1 text-sm text-muted-foreground">
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              <span className="line-clamp-1">{trip.destination}</span>
            </div>

            {/* Date + Days + Status */}
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <div className="flex items-center gap-1 text-muted-foreground">
                <Calendar className="h-3.5 w-3.5 shrink-0" />
                <span>{dateInfo.formatted}</span>
                {dateInfo.days !== null && (
                  <span className="text-xs text-muted-foreground/70">({dateInfo.days}日間)</span>
                )}
              </div>
              <Badge className={cn("px-1.5 py-0 text-[10px]", statusConfig.className)}>
                {statusConfig.label}
              </Badge>
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
}
